import clsx from 'clsx';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Typography } from '~/shared/components/Typography';
import { CountdownColorTheme, useCountdown, useCountdownTheme } from '~/shared/hooks/countdown';
import { trustedTime } from '~/shared/utils/trusted-time';
import styles from './Countdown.module.scss';

export type CountdownVariant = 'cta' | 'hero';

export type CountdownProps = {
    endDate: string | Date;
    variant?: CountdownVariant;
    /** Optional heading above the timer / expired state (CTA variant) */
    headline?: string;
    /** Replaces digit units when the countdown has expired */
    expiredText?: string;
    /**
     * Delay (ms) before swapping digits to `expiredText` after the timer hits zero.
     * Default: `1000` when `expiredText` is provided, otherwise `0`.
     */
    expiredTextDelayMs?: number;
    /**
     * Optional theme override.
     * When omitted, we fall back to CMS `settings.countdownColorTheme`.
     * Final fallback is `defaultColorTheme`.
     */
    colorTheme?: CountdownColorTheme | null;
    className?: string;
    onExpire?: () => void;
};

const COUNTDOWN_ITEMS = [
    { key: 'days', label: 'Dage' },
    { key: 'hours', label: 'Timer' },
    { key: 'minutes', label: 'Minutter' },
    { key: 'seconds', label: 'Sekunder' },
] as const;

const DEFAULT_TIMER_LABEL = 'Countdown';

export const Countdown = ({
    endDate,
    variant = 'cta',
    headline,
    expiredText,
    expiredTextDelayMs,
    colorTheme,
    className,
    onExpire,
}: CountdownProps) => {
    const headlineId = useId();
    const { timeLeft, isExpired, hasMounted } = useCountdown({ endDate, onExpire });
    const themeStyle = useCountdownTheme(colorTheme);
    const isCtaVariant = variant === 'cta';
    const delayMs = expiredTextDelayMs ?? (expiredText ? 1000 : 0);
    const [shouldShowExpiredText, setShouldShowExpiredText] = useState(false);
    const expiredTextTimeoutRef = useRef<number | null>(null);
    // True only if we observed remaining time while mounted (live expire → keep delay).
    const hadRemainingRef = useRef(false);

    useEffect(() => {
        hadRemainingRef.current = false;
        setShouldShowExpiredText(false);
    }, [endDate]);

    useEffect(() => {
        const hasRemaining =
            !isExpired && timeLeft.days + timeLeft.hours + timeLeft.minutes + timeLeft.seconds > 0;
        if (hasRemaining) {
            hadRemainingRef.current = true;
        }
    }, [isExpired, timeLeft]);

    useLayoutEffect(() => {
        // Digits hit 00 immediately via useCountdown. Delay LIVE only for live expire;
        // remount when already past → show expiredText immediately (still hydrate-safe).
        if (!expiredText || !isExpired) {
            if (!isExpired) setShouldShowExpiredText(false);
            return;
        }

        const effectiveDelayMs = hadRemainingRef.current ? Math.max(delayMs, 0) : 0;
        if (effectiveDelayMs === 0) {
            setShouldShowExpiredText(true);
            return;
        }

        expiredTextTimeoutRef.current = window.setTimeout(() => {
            setShouldShowExpiredText(true);
        }, effectiveDelayMs);

        return () => {
            if (expiredTextTimeoutRef.current) {
                window.clearTimeout(expiredTextTimeoutRef.current);
                expiredTextTimeoutRef.current = null;
            }
        };
    }, [delayMs, expiredText, isExpired]);

    // Already past at render (persist remount): show LIVE on first paint — SSR/client agree when clearly past.
    // Active countdown: blank digit boxes until mount. Live expire: keep delay via hadRemainingRef.
    const isPastEnd = new Date(endDate).getTime() <= trustedTime.now();
    const showExpiredState =
        Boolean(expiredText) &&
        (shouldShowExpiredText ||
            (!hadRemainingRef.current && (isPastEnd || (hasMounted && isExpired))));
    // Blank digit boxes only while hydrating an active (not-yet-past) countdown.
    const hideDigitsUntilHydrated = !hasMounted && !isPastEnd;
    const hasHeadline = Boolean(headline?.trim());

    const namingProps = hasHeadline
        ? ({ 'aria-labelledby': headlineId } as const)
        : ({ 'aria-label': DEFAULT_TIMER_LABEL } as const);

    const timerA11yProps = showExpiredState
        ? ({
              role: 'status',
              'aria-live': 'polite',
              'aria-atomic': true,
              'aria-hidden': !hasMounted && !isPastEnd,
              ...namingProps,
          } as const)
        : ({
              role: 'timer',
              'aria-atomic': true,
              'aria-hidden': !hasMounted,
              ...namingProps,
          } as const);

    if (!isCtaVariant) {
        return (
            <div
                className={clsx(styles.countdown, styles.countdownHero, className)}
                style={themeStyle}
                {...timerA11yProps}
            >
                {COUNTDOWN_ITEMS.map((item) => (
                    <div key={item.key} className={clsx(styles.card, styles.cardHero)}>
                        <span className={clsx(styles.value, styles.valueHero)}>
                            {String(timeLeft[item.key]).padStart(2, '0')}
                        </span>
                        <span className={clsx(styles.label, styles.labelHero)}>{item.label}</span>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className={clsx(styles.rootCta, className)} style={themeStyle}>
            {hasHeadline ? (
                <Typography
                    id={headlineId}
                    className={styles.headline}
                    variant="paragraph-S"
                    as="p"
                    weight="medium"
                >
                    {headline}
                </Typography>
            ) : null}

            <div className={clsx(styles.countdown, styles.countdownCta)} {...timerA11yProps}>
                {showExpiredState ? (
                    <div className={clsx(styles.card, styles.cardCta, styles.expiredCard)}>
                        <span className={styles.expiredText}>{expiredText}</span>
                    </div>
                ) : (
                    COUNTDOWN_ITEMS.map((item) => (
                        <div key={item.key} className={clsx(styles.card, styles.cardCta)}>
                            {/* Active countdown hydrate: blank digit boxes (not LIVE bar). */}
                            <span
                                className={clsx(styles.value, styles.valueCta)}
                                style={
                                    hideDigitsUntilHydrated ? { visibility: 'hidden' } : undefined
                                }
                            >
                                {String(timeLeft[item.key]).padStart(2, '0')}
                            </span>

                            <Typography
                                className={clsx(styles.label, styles.labelCta)}
                                variant="paragraph-S"
                                weight="medium"
                            >
                                {item.label}
                            </Typography>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
